import React, { useEffect, useRef, useState } from "react";
import {
  PAGE_HEIGHT_INCHES,
  PAGE_WIDTH_INCHES,
  VOUCHER_BANDS,
  VOUCHER_GUIDES_INCHES,
  inchesToPixels,
  pageLayoutBandHeight,
  pixelsToInches,
  snapInches,
} from "../../../utility/checkGeometry";
import {
  CHECK_FIELD_TYPE_LABELS,
  fieldDisplayValue,
} from "../../../utility/checkFieldTypes";
import PrizeLinesTable from "./PrizeLinesTable";

const HANDLE = 10;

const CheckPageCanvas = ({
  pageLayout,
  fields,
  selectedId,
  scanUrl,
  showPreview,
  previewValues,
  onSelect,
  onChangeField,
}) => {
  const pageRef = useRef(null);
  const [pageWidthPx, setPageWidthPx] = useState(680);
  const pixelsPerInch = pageWidthPx / PAGE_WIDTH_INCHES;
  const pageHeightPx = inchesToPixels(PAGE_HEIGHT_INCHES, pixelsPerInch);
  const bandHeight = pageLayoutBandHeight(pageLayout);
  const dragRef = useRef(null);

  useEffect(() => {
    const node = pageRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setPageWidthPx(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const startDrag = (event, field, mode) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(field.id);
    dragRef.current = {
      id: field.id,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      originX: field.xInches,
      originY: field.yInches,
      originW: field.widthInches,
      originH: field.heightInches,
    };
  };

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = pixelsToInches(event.clientX - drag.startX, pixelsPerInch);
      const dy = pixelsToInches(event.clientY - drag.startY, pixelsPerInch);
      if (drag.mode === "move") {
        onChangeField(drag.id, {
          xInches: snapInches(drag.originX + dx),
          yInches: snapInches(drag.originY + dy),
        });
      } else {
        onChangeField(drag.id, {
          widthInches: snapInches(drag.originW + dx),
          heightInches: snapInches(drag.originH + dy),
        });
      }
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onChangeField, pixelsPerInch]);

  const ghostOffsets =
    pageLayout === "threePerPage" ? [bandHeight, bandHeight * 2] : [];
  const guides =
    pageLayout === "voucher"
      ? VOUCHER_GUIDES_INCHES
      : [bandHeight, bandHeight * 2];
  const bandLabels =
    pageLayout === "threePerPage"
      ? [{ label: "Check — edit this band", topInches: 0 }]
      : VOUCHER_BANDS.map((band) => ({
          label: band.label,
          topInches: band.topInches,
        }));

  return (
    <div
      ref={pageRef}
      className="relative w-full bg-white border border-brand-charcoal/30 shadow-sm overflow-hidden"
      style={{
        width: "100%",
        height: pageHeightPx,
        backgroundImage: scanUrl ? `url(${scanUrl})` : "none",
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
      }}
      onPointerDown={() => onSelect(null)}
      role="application"
      aria-label="Check page"
    >
      {!scanUrl ? (
        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:10%_9.09%]" />
      ) : null}

      {guides.map((guide) => (
        <div
          key={guide}
          className="absolute left-0 right-0 border-t border-dashed border-brand-gold-ink/70 pointer-events-none"
          style={{ top: inchesToPixels(guide, pixelsPerInch) }}
        />
      ))}

      {bandLabels.map((band) => (
        <div
          key={band.label}
          className="absolute left-1 text-[10px] uppercase tracking-wide text-brand-gold-ink/80 pointer-events-none"
          style={{ top: inchesToPixels(band.topInches, pixelsPerInch) + 4 }}
        >
          {band.label}
        </div>
      ))}

      {fields.map((field) => {
        const isPrizeLines = field.fieldType === "prizeLines";
        const fontSize = Math.max(8, field.fontSizePt * (pixelsPerInch / 72));
        const label = showPreview
          ? fieldDisplayValue(field, previewValues)
          : CHECK_FIELD_TYPE_LABELS[field.fieldType] || field.fieldType;
        const selected = field.id === selectedId;
        const prizeTable = isPrizeLines ? (
            <div
              className="h-full box-border pointer-events-none"
              style={{ padding: "3px 2px 2px" }}
            >
              <PrizeLinesTable
                field={field}
                values={previewValues}
                fontSize={fontSize}
              />
            </div>
          ) : null;
        return (
          <React.Fragment key={field.id}>
            <div
              className={`absolute border ${
                selected
                  ? "border-brand-gold-ink bg-brand-gold/20"
                  : "border-brand-charcoal/50 bg-white/80"
              }`}
              style={{
                left: inchesToPixels(field.xInches, pixelsPerInch),
                top: inchesToPixels(field.yInches, pixelsPerInch),
                width: inchesToPixels(field.widthInches, pixelsPerInch),
                height: inchesToPixels(field.heightInches, pixelsPerInch),
                fontSize,
                fontWeight: isPrizeLines ? 400 : field.fontBold ? 700 : 400,
                fontStyle: field.fontItalic ? "italic" : "normal",
                textAlign: field.textAlign,
                fontFamily: "Helvetica, Arial, sans-serif",
                lineHeight: isPrizeLines ? 1.25 : 1,
                overflow: "hidden",
                whiteSpace: isPrizeLines
                  ? "normal"
                  : field.overflow === "wrap"
                    ? "pre-line"
                    : "nowrap",
                cursor: "move",
              }}
              onPointerDown={(event) => startDrag(event, field, "move")}
            >
              {prizeTable ? (
                prizeTable
              ) : (
                <span
                  className={`block ${
                    isPrizeLines || field.overflow === "wrap" ? "" : "truncate"
                  }`}
                >
                  {label || " "}
                </span>
              )}
              {selected ? (
                <span
                  className="absolute bg-brand-gold-ink"
                  style={{
                    width: HANDLE,
                    height: HANDLE,
                    right: -HANDLE / 2,
                    bottom: -HANDLE / 2,
                    cursor: "nwse-resize",
                  }}
                  onPointerDown={(event) => startDrag(event, field, "resize")}
                />
              ) : null}
            </div>
            {ghostOffsets.map((offset) => (
              <div
                key={`${field.id}-${offset}`}
                className="absolute border border-dashed border-brand-charcoal/30 bg-white/40 pointer-events-none"
                style={{
                  left: inchesToPixels(field.xInches, pixelsPerInch),
                  top: inchesToPixels(field.yInches + offset, pixelsPerInch),
                  width: inchesToPixels(field.widthInches, pixelsPerInch),
                  height: inchesToPixels(field.heightInches, pixelsPerInch),
                  fontSize,
                  opacity: 0.45,
                  overflow: "hidden",
                }}
              >
                {prizeTable ? (
                  prizeTable
                ) : (
                  <span className="block truncate">{label || " "}</span>
                )}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CheckPageCanvas;
